import React, { useState, useEffect } from 'react';
import { X, Newspaper, BookOpen, FileText, Search, Loader2, Sparkles, Check, ArrowRight } from 'lucide-react';
import { themes } from '../../utils/themes';
import { db } from '../../lib/firebase';
import { ref, get, set } from 'firebase/database';
import { API_BASE_URL } from '../../config/api';
import { SessionStudyMaterial } from '../../services/friendSessionService';

interface MaterialSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMaterial: (material: Omit<SessionStudyMaterial, 'updatedAt' | 'updatedBy' | 'updatedByName'>) => void;
  initialTab?: 'news' | 'theme' | 'custom';
}

const NEWS_TOPICS = [
  'technology',
  'science',
  'world',
  'health',
  'entertainment',
  'economy',
  'music',
];

export const MaterialSelectorModal: React.FC<MaterialSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelectMaterial,
  initialTab,
}) => {
  const [activeTab, setActiveTab] = useState<'news' | 'theme' | 'custom'>(initialTab || 'custom');

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen]);

  // Custom text state
  const [customTitle, setCustomTitle] = useState('');
  const [customContent, setCustomContent] = useState('');

  // Theme selection state
  const [themeSearch, setThemeSearch] = useState('');
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);

  // News selection state
  const [newsTopic, setNewsTopic] = useState('technology');
  const [loadingNews, setLoadingNews] = useState(false);
  const [newsArticles, setNewsArticles] = useState<any[]>([]);

  // Fetch news from RTDB cache or live feed API
  useEffect(() => {
    if (activeTab !== 'news') return;

    let isMounted = true;
    setLoadingNews(true);

    const fetchNews = async () => {
      try {
        // 1. Check recent RTDB cache up to 7 days
        let foundInCache = false;
        for (let offset = 0; offset <= 7; offset++) {
          const targetDate = new Date();
          targetDate.setDate(targetDate.getDate() - offset);
          const dateStr = targetDate.toISOString().split('T')[0];
          try {
            const cacheRef = ref(db, `newsArticles/live_feed_cache/${dateStr}_${newsTopic}`);
            const snap = await get(cacheRef);
            if (snap.exists() && isMounted) {
              const val = snap.val();
              if (Array.isArray(val) && val.length > 0) {
                setNewsArticles(val);
                setLoadingNews(false);
                foundInCache = true;
                break;
              }
            }
          } catch {
            // continue checking
          }
        }

        // 2. If not found in cache, fetch directly from live news feed API
        if (!foundInCache) {
          try {
            const res = await fetch(`${API_BASE_URL}/api/news/feed?type=ai&topics=${encodeURIComponent(newsTopic)}&page=1`);
            const data = await res.json();
            if (data.success && Array.isArray(data.articles) && data.articles.length > 0 && isMounted) {
              setNewsArticles(data.articles);
              setLoadingNews(false);
              const cacheDateStr = data.date || new Date().toISOString().split('T')[0];
              set(ref(db, `newsArticles/live_feed_cache/${cacheDateStr}_${newsTopic}`), data.articles).catch(console.error);
              return;
            }
          } catch (apiErr) {
            console.error('Error fetching live news feed from API:', apiErr);
          }
        }

        if (isMounted && !foundInCache) {
          setNewsArticles([]);
          setLoadingNews(false);
        }
      } catch (err) {
        console.error('Error in session news workflow:', err);
        if (isMounted) {
          setLoadingNews(false);
        }
      }
    };

    fetchNews();

    return () => {
      isMounted = false;
    };
  }, [activeTab, newsTopic]);

  if (!isOpen) return null;

  const handleSelectNews = (article: any) => {
    const title = article.title || 'News Article';
    // Clean and split content into paragraphs
    const rawContent = article.content || article.description || article.summary || '';
    const paragraphs = rawContent
      .split(/\n\s*\n/)
      .map((p: string) => p.trim())
      .filter((p: string) => p.length > 0);

    const cleanContent = paragraphs.length > 0 ? paragraphs.join('\n\n') : rawContent;

    onSelectMaterial({
      type: 'news',
      title,
      content: cleanContent,
      topic: newsTopic,
    });
    onClose();
  };

  const handleSelectTheme = (themeId: string) => {
    const theme = themes.find((t) => t.id === themeId);
    if (!theme) return;

    // Build study article incorporating theme vocabulary and example sentences
    const sentenceList = theme.sentences && theme.sentences.length > 0
      ? theme.sentences.slice(0, 8).join(' ')
      : '';
    const vocabList = theme.words ? theme.words.slice(0, 15).join(', ') : '';

    const content = [
      `Theme: ${theme.name}. ${theme.description || 'Practice this curated set of everyday vocabulary and expressions.'}`,
      sentenceList ? `Key Sentences for Practice:\n${theme.sentences?.slice(0, 6).map((s, idx) => `${idx + 1}. ${s}`).join('\n')}` : '',
      vocabList ? `Vocabulary Target Words: ${vocabList}.` : '',
    ].filter(Boolean).join('\n\n');

    onSelectMaterial({
      type: 'theme',
      title: `${theme.icon || '📚'} ${theme.name}`,
      content,
      themeId: theme.id,
      topic: theme.category,
    });
    onClose();
  };

  const handleApplyCustomText = () => {
    if (!customContent.trim()) return;

    const title = customTitle.trim() || 'Custom Study Article';
    // Format text into clear paragraphs
    const paragraphs = customContent
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);

    const formattedContent = paragraphs.length > 0 ? paragraphs.join('\n\n') : customContent.trim();

    onSelectMaterial({
      type: 'custom_text',
      title,
      content: formattedContent,
    });
    onClose();
  };

  const filteredThemes = themes.filter((t) =>
    t.name.toLowerCase().includes(themeSearch.toLowerCase()) ||
    t.category.toLowerCase().includes(themeSearch.toLowerCase()) ||
    (t.description && t.description.toLowerCase().includes(themeSearch.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Choose Study Material
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Select or paste content to study and sync in real time with your friend
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-white/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 bg-gray-50/70 p-1 gap-1">
          <button
            onClick={() => setActiveTab('custom')}
            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
              activeTab === 'custom'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <FileText className="w-4 h-4" />
            Paste / Custom Text
          </button>
          <button
            onClick={() => setActiveTab('news')}
            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
              activeTab === 'news'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Newspaper className="w-4 h-4" />
            Live News
          </button>
          <button
            onClick={() => setActiveTab('theme')}
            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
              activeTab === 'theme'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Curated Themes
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* TAB 1: Custom Text */}
          {activeTab === 'custom' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                  Article Title (Optional)
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="e.g., An Inspiring Journey to Mars, Tech Trends 2026..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Article Text / Story with Paragraphs
                  </label>
                  <span className="text-xs text-gray-400">
                    {customContent.split(/\s+/).filter(Boolean).length} words
                  </span>
                </div>
                <textarea
                  rows={8}
                  value={customContent}
                  onChange={(e) => setCustomContent(e.target.value)}
                  placeholder="Type or paste any text or article with paragraphs here...

Each paragraph will be displayed cleanly so you and your friend can click any word or select an expression to inspect with WordPopover and save!"
                  className="w-full px-3.5 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm font-sans leading-relaxed"
                />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={handleApplyCustomText}
                  disabled={!customContent.trim()}
                  className="px-6 py-2.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                >
                  <span>Sync Text to Session</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: News */}
          {activeTab === 'news' && (
            <div className="space-y-4">
              {/* Topic chips */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none">
                {NEWS_TOPICS.map((topic) => (
                  <button
                    key={topic}
                    onClick={() => setNewsTopic(topic)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize whitespace-nowrap transition-colors ${
                      newsTopic === topic
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {topic}
                  </button>
                ))}
              </div>

              {loadingNews ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-2" />
                  <p className="text-xs">Loading latest news articles...</p>
                </div>
              ) : newsArticles.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200 p-6">
                  <Newspaper className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-600">No cached news found for {newsTopic}</p>
                  <p className="text-xs text-gray-400 mt-1">Try another topic or paste custom text in the first tab!</p>
                </div>
              ) : (
                <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1">
                  {newsArticles.map((article, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectNews(article)}
                      className="p-3.5 border border-gray-100 rounded-xl hover:border-purple-300 hover:bg-purple-50/40 cursor-pointer transition-all bg-white group shadow-xs"
                    >
                      <h4 className="font-semibold text-gray-900 group-hover:text-purple-700 text-sm mb-1 line-clamp-2">
                        {article.title}
                      </h4>
                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                        {article.description || article.summary || article.contentSnippet || article.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Themes */}
          {activeTab === 'theme' && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                <input
                  type="text"
                  value={themeSearch}
                  onChange={(e) => setThemeSearch(e.target.value)}
                  placeholder="Search themes (Travel, Work, Food, Technology...)"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[360px] overflow-y-auto pr-1">
                {filteredThemes.map((theme) => (
                  <div
                    key={theme.id}
                    onClick={() => handleSelectTheme(theme.id)}
                    className="p-3 border border-gray-100 rounded-xl hover:border-purple-300 hover:bg-purple-50/40 cursor-pointer transition-all bg-white group shadow-xs"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{theme.icon}</span>
                      <h4 className="font-bold text-gray-900 group-hover:text-purple-700 text-sm truncate">
                        {theme.name}
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {theme.description || `${theme.words.length} vocabulary words ready for practice.`}
                    </p>
                    <div className="mt-2 text-[11px] text-purple-600 font-medium">
                      {theme.words.length} words • {theme.sentences?.length || 0} sentences
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
